import { processListingParams } from '@/api/helpers';
import {
  BulkLaunchCampaignsResponse,
  Campaign,
  CampaignRecommendationRequest,
  CampaignRecommendationResult,
  CampaignStatusCounts,
  CampaignSummary,
  CloneCampaignRequest,
  CreateAutoPhishingCampaignRequest,
  CreateAutoPhishingCampaignsRequest,
  ExportCampaignsReportRequest,
  GetCampaignRequest,
  LanguageFailureNotice,
  LanguageGroupResult,
  ListCampaignsRequest,
  ListCampaignsResponse,
  ListCampaignUserResultsRequest,
  ListCampaignUserResultsResponse,
  MigrationCampaignRequest,
  PlaceholderLikelyPick,
  PlaceholderSelectionPreview,
  SaveCampaignRequest,
  SendTestEmail,
  SimulationsSummary,
  UpdateCampaignAction,
  UpdateCampaignRequest,
  UpdateLanguageGroupPayload,
} from '@/client/campaigns';
import { getExceptionFromAxiosError } from '@/client/helpers';
import { MessageResponseModel } from '@/client/models';
import { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { omit } from 'lodash';

export const campaignsService = (client: AxiosInstance) => {
  const CAMPAIGNS_BASE_URL = '/gophish/campaigns';

  const getCampaigns = async (
    params: ListCampaignsRequest,
  ): Promise<ListCampaignsResponse> => {
    try {
      const result = await client.get<
        ListCampaignsResponse,
        AxiosResponse<ListCampaignsResponse>
      >(CAMPAIGNS_BASE_URL, {
        withCredentials: true,
        params: processListingParams(params),
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getCampaignsSummary = async (
    accountId: string,
    showIndividualCampaigns?: 0 | 1,
  ): Promise<CampaignSummary> => {
    try {
      const result = await client.get<
        CampaignSummary,
        AxiosResponse<CampaignSummary>
      >(`${CAMPAIGNS_BASE_URL}/accounts/${accountId}/summary`, {
        withCredentials: true,
        params:
          showIndividualCampaigns !== undefined
            ? { showIndividualCampaigns }
            : undefined,
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getCampaignUserResults = async (
    params: ListCampaignUserResultsRequest,
    campaignId?: string,
  ): Promise<ListCampaignUserResultsResponse> => {
    try {
      const result = await client.get<
        ListCampaignUserResultsResponse,
        AxiosResponse<ListCampaignUserResultsResponse>
      >(`${CAMPAIGNS_BASE_URL}/${campaignId}/results`, {
        withCredentials: true,
        params: processListingParams(params),
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getCampaign = async (
    campaignId: string,
    params?: GetCampaignRequest,
  ): Promise<Campaign> => {
    try {
      const result = await client.get<Campaign, AxiosResponse<Campaign>>(
        `${CAMPAIGNS_BASE_URL}/${campaignId}`,
        {
          withCredentials: true,
          params: processListingParams(params),
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const saveCampaignAction = async ({
    campaignId,
    action,
  }: UpdateCampaignAction) => {
    try {
      const result = await client.post<
        MessageResponseModel,
        AxiosResponse<MessageResponseModel>
      >(`${CAMPAIGNS_BASE_URL}/${campaignId}/actions/${action}`);

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const saveCampaign = async (
    request: SaveCampaignRequest,
  ): Promise<Campaign> => {
    try {
      const result = await client.post(CAMPAIGNS_BASE_URL, request, {
        withCredentials: true,
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const copyCampaign = async (
    request: CloneCampaignRequest,
  ): Promise<Campaign> => {
    try {
      const result = await client.post(
        `${CAMPAIGNS_BASE_URL}/${request.campaignId}/copy`,
        { account: request.account },
        {
          withCredentials: true,
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const cloneCampaign = async (
    request: CloneCampaignRequest,
  ): Promise<Campaign> => {
    try {
      const result = await client.post(
        `${CAMPAIGNS_BASE_URL}/${request.campaignId}/clone`,
        { account: request.account },
        {
          withCredentials: true,
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const sendTestEmail = async (
    request: SendTestEmail,
  ): Promise<{ jobId: string } & MessageResponseModel> => {
    try {
      const result = await client.post<
        { jobId: string } & MessageResponseModel,
        AxiosResponse<{ jobId: string } & MessageResponseModel>
      >(
        `${CAMPAIGNS_BASE_URL}/${request.campaignId}/test-email`,
        omit(request, 'campaignId'),
        {
          withCredentials: true,
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const updateCampaign = async (
    request: UpdateCampaignRequest,
  ): Promise<Campaign> => {
    try {
      const result = await client.patch(
        `${CAMPAIGNS_BASE_URL}/${request.campaignId}`,
        request.updates,
        {
          withCredentials: true,
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const deleteCampaign = async (
    campaignId: string,
  ): Promise<MessageResponseModel> => {
    try {
      const result = await client.delete<
        MessageResponseModel,
        AxiosResponse<MessageResponseModel>
      >(`${CAMPAIGNS_BASE_URL}/${campaignId}`, {
        withCredentials: true,
      });

      return new MessageResponseModel(result.data);
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const migrateCampaign = async (
    request: MigrationCampaignRequest,
  ): Promise<MessageResponseModel> => {
    try {
      const result = await client.post(
        `${CAMPAIGNS_BASE_URL}/migration`,
        request,
        {
          withCredentials: true,
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const syncCampaignResults = async (
    campaignId: string,
  ): Promise<MessageResponseModel> => {
    try {
      const result = await client.post<
        MessageResponseModel,
        AxiosResponse<MessageResponseModel>
      >(`${CAMPAIGNS_BASE_URL}/${campaignId}/results/sync`, {
        withCredentials: true,
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const exportPhishingReport = async (
    campaignId: string,
    request: ExportCampaignsReportRequest,
  ): Promise<MessageResponseModel> => {
    try {
      const result = await client.post<
        MessageResponseModel,
        AxiosResponse<MessageResponseModel>
      >(
        `${CAMPAIGNS_BASE_URL}/${campaignId}/results/export`,
        { ...request },
        {
          withCredentials: true,
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const campaignsNotifyUrl = (campaignId: string) =>
    `${client.defaults.baseURL}gophish/campaigns/${campaignId}/notify`;

  const campaingsAccountNotifyUrl = (accountId: string) =>
    `${client.defaults.baseURL}gophish/accounts/${accountId}/campaigns/notify`;

  const autoPhishingCampaignsNotifyUrl = (jobId: string, accountId: string) =>
    `${client.defaults.baseURL}gophish/accounts/${accountId}/auto-phishing-campaigns/notify/${jobId}`;

  const recommendCampaign = async (
    accountId: string,
    params: CampaignRecommendationRequest,
  ): Promise<CampaignRecommendationResult> => {
    try {
      const result = await client.get<
        CampaignRecommendationResult,
        AxiosResponse<CampaignRecommendationResult>
      >(`accounts/${accountId}/campaigns/recommend`, {
        withCredentials: true,
        params,
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getCampaignStatusCounts = async (
    accountId: string,
  ): Promise<CampaignStatusCounts> => {
    try {
      const result = await client.get<
        CampaignStatusCounts,
        AxiosResponse<CampaignStatusCounts>
      >(`/gophish/accounts/${accountId}/campaign-status-counts`, {
        withCredentials: true,
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getSimulationsSummary = async (
    accountId: string,
    params?: ListCampaignsRequest,
  ): Promise<SimulationsSummary> => {
    try {
      const result = await client.get<
        SimulationsSummary,
        AxiosResponse<SimulationsSummary>
      >(`/gophish/accounts/${accountId}/simulations-summary`, {
        withCredentials: true,
        params: processListingParams(params),
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const createAutoPhishingCampaign = async (
    accountId: string,
    payload: CreateAutoPhishingCampaignRequest,
  ): Promise<Campaign> => {
    try {
      const result = await client.post<Campaign, AxiosResponse<Campaign>>(
        `/gophish/accounts/${accountId}/auto-phishing-campaigns`,
        payload,
        { withCredentials: true },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const launchAutoPhishingCampaigns = async (
    accountId: string,
    campaignIds: string[],
  ): Promise<BulkLaunchCampaignsResponse> => {
    try {
      const result = await client.post<
        BulkLaunchCampaignsResponse,
        AxiosResponse<BulkLaunchCampaignsResponse>
      >(
        `/gophish/accounts/${accountId}/auto-phishing-campaigns/launch`,
        { campaignIds },
        { withCredentials: true },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const createAutoPhishingCampaigns = async (
    accountId: string,
    payload: CreateAutoPhishingCampaignsRequest,
  ): Promise<{ message: string; jobId: string } & MessageResponseModel> => {
    try {
      const result = await client.post<
        { message: string; jobId: string } & MessageResponseModel,
        AxiosResponse<{ message: string; jobId: string } & MessageResponseModel>
      >(
        `/gophish/accounts/${accountId}/auto-phishing-campaigns/ai-generate`,
        payload,
        { withCredentials: true },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getPlaceholderSelectionPreview = async (
    accountId: string,
  ): Promise<PlaceholderSelectionPreview[]> => {
    try {
      const result = await client.get<
        PlaceholderSelectionPreview[],
        AxiosResponse<PlaceholderSelectionPreview[]>
      >(
        `/gophish/accounts/${accountId}/auto-phishing-campaigns/placeholder-preview`,
        { withCredentials: true },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const generatePlaceholderLikelyPick = async (
    accountId: string,
    placeholderId: string,
  ): Promise<PlaceholderLikelyPick> => {
    try {
      const result = await client.post<
        PlaceholderLikelyPick,
        AxiosResponse<PlaceholderLikelyPick>
      >(
        `/gophish/accounts/${accountId}/auto-phishing-campaigns/placeholder-preview/${placeholderId}/likely-pick`,
        undefined,
        { withCredentials: true },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const updateLanguageGroup = async (
    accountId: string,
    languageGroupId: string,
    payload: UpdateLanguageGroupPayload,
  ): Promise<LanguageGroupResult> => {
    try {
      const result = await client.patch<
        LanguageGroupResult,
        AxiosResponse<LanguageGroupResult>
      >(
        `/gophish/accounts/${accountId}/auto-phishing-campaigns/language-groups/${languageGroupId}`,
        payload,
        { withCredentials: true },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const deleteLanguageGroup = async (
    accountId: string,
    languageGroupId: string,
  ): Promise<LanguageGroupResult> => {
    try {
      const result = await client.delete<
        LanguageGroupResult,
        AxiosResponse<LanguageGroupResult>
      >(
        `/gophish/accounts/${accountId}/auto-phishing-campaigns/language-groups/${languageGroupId}`,
        { withCredentials: true },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getLanguageFailureNotices = async (
    accountId: string,
  ): Promise<LanguageFailureNotice[]> => {
    try {
      const result = await client.get<
        LanguageFailureNotice[],
        AxiosResponse<LanguageFailureNotice[]>
      >(
        `/gophish/accounts/${accountId}/auto-phishing-campaigns/language-failure-notices`,
        { withCredentials: true },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  return {
    getCampaigns,
    getCampaign,
    getCampaignsSummary,
    getCampaignUserResults,
    sendTestEmail,
    saveCampaignAction,
    saveCampaign,
    copyCampaign,
    cloneCampaign,
    updateCampaign,
    deleteCampaign,
    migrateCampaign,
    syncCampaignResults,
    exportPhishingReport,
    campaignsNotifyUrl,
    campaingsAccountNotifyUrl,
    autoPhishingCampaignsNotifyUrl,
    recommendCampaign,
    getCampaignStatusCounts,
    getSimulationsSummary,
    createAutoPhishingCampaign,
    launchAutoPhishingCampaigns,
    createAutoPhishingCampaigns,
    getPlaceholderSelectionPreview,
    generatePlaceholderLikelyPick,
    updateLanguageGroup,
    deleteLanguageGroup,
    getLanguageFailureNotices,
  };
};
